import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import { apiErrorMessage } from './prescriptionForm';
import {
  topSet, dayTrend, daySummary, progressNote, sinceDays,
} from './trainingHistory';
import '../styles/TrainingHistoryPage.css';

// The athlete's own training, read back to them.
//
// # WHY THIS SCREEN EXISTS
//
// A working history view was already built and nothing linked to it — the
// finding in docs/mvp-funnel-2026-09-21.md §4. That document recommended
// extracting and linking it; this does not, because its fetch reads performances
// by exercise_entry_id and entries are copied when a block is assigned. It shows
// one session inside ONE block, and empties itself the day the next one starts.
//
// Identity here is the movement, which survives the copy.

/** The movement picker: what this athlete has actually trained. */
const MovementList = ({ movements, selected, onSelect }) => (
  <ul className="history-movements">
    {movements.map((movement) => (
      <li key={movement.templateId}>
        <button
          type="button"
          className={`history-movement${
            movement.templateId === selected ? ' is-selected' : ''
          }`}
          aria-current={movement.templateId === selected ? 'true' : undefined}
          onClick={() => onSelect(movement.templateId)}
        >
          <span className="history-movement-name">{movement.exerciseName}</span>
          <span className="history-movement-when muted">
            {sinceDays(movement.lastLoggedOn) ?? movement.lastLoggedOn}
          </span>
        </button>
      </li>
    ))}
  </ul>
);

/*
 * One day of work, with how its top set compares with the day before.
 *
 * The arrow is on the DAY rather than in a summary at the top, because the
 * question "am I getting stronger" is answered by the shape of the column, not
 * by one number — and a single headline figure invites reading session noise as
 * a trend.
 */
const TRENDS = {
  up: { mark: '▲', word: 'up on', className: 'is-up' },
  down: { mark: '▼', word: 'down on', className: 'is-down' },
  same: { mark: '=', word: 'unchanged on', className: 'is-same' },
};

const HistoryDay = ({ day, unit, trend }) => {
  const mark = trend ? TRENDS[trend.direction] : null;
  // The arrow and the screen-reader sentence must name the SAME basis the arrow
  // was computed from. An arrow drawn off the estimate and read out as "top set"
  // is a different claim to sighted and unsighted readers of one row.
  const basis = trend?.basis === 'estimate' ? 'estimated single' : 'top set';

  return (
    <li className={`history-entry${mark ? ` ${mark.className}` : ''}`}>
      <div className="history-entry-head">
        <span className="history-entry-date">{day.date}</span>
        {mark && (
          <span className="history-entry-trend">
            <span aria-hidden="true">{mark.mark}</span>
            <span className="sr-only">{`${mark.word} ${basis} since last time`}</span>
          </span>
        )}
      </div>
      <p className="meta-copy history-entry-summary">
        {daySummary(day, unit)}
        {/* Marked as an estimate in the text itself, not only by position.
            It is inferred from a set that was performed, and the table of
            recorded maxes deliberately holds the tested kind — see
            models/repmax.go. A number that reads as measured when it was
            calculated erodes exactly that distinction. */}
        {day.estimatedMax != null && (
          <span className="history-entry-estimate">
            {' · ~'}{day.estimatedMax} {unit} single
          </span>
        )}
      </p>
      <ul className="history-entry-sets">
        {day.sets.map((set) => (
          <li key={set.setNumber} className="history-entry-set mono">
            <span className="muted">{set.setNumber}</span>
            {set.durationSeconds != null ? (
              <span>{set.durationSeconds}s</span>
            ) : set.distanceMetres != null ? (
              <span>{set.distanceMetres} m</span>
            ) : (
              <>
                {/* Bodyweight is a real logged load. Rendered as "0 kg" it
                    reads as a failed lift, so it is named instead. */}
                <span>{set.weight > 0 ? `${set.weight} ${unit}` : 'Bodyweight'}</span>
                <span>
                  <span aria-hidden="true">×</span>
                  {set.repsPerformed}
                  <span className="sr-only"> reps</span>
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
};

const TrainingHistoryPage = () => {
  const { token } = useAuth();

  const [movements, setMovements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(buildApiUrl('/training/history'), authConfig());
        if (!live) return;
        const list = data?.movements ?? [];
        setMovements(list);
        // Open on the movement they trained most recently, rather than on a
        // picker with nothing chosen: one fewer decision to reach the answer.
        if (list.length > 0) setSelected(list[0].templateId);
      } catch (err) {
        if (live) setError(apiErrorMessage(err, 'Could not load your training history.'));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [authConfig]);

  useEffect(() => {
    if (!selected) return undefined;
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(
          buildApiUrl(`/training/history/${encodeURIComponent(selected)}`),
          authConfig()
        );
        if (live) setHistory(data);
      } catch (err) {
        if (live) {
          setHistory(null);
          setError(apiErrorMessage(err, 'Could not load that movement.'));
        }
      }
    })();
    return () => { live = false; };
  }, [selected, authConfig]);

  if (loading) {
    return <BaseLayout><p className="history-status">Loading your lifts…</p></BaseLayout>;
  }

  const note = progressNote(history);
  const unit = history?.unit ?? 'kg';

  return (
    <BaseLayout>
      <div className="training-history">
        {/* "Your lifts", not "Your training".
         *
         * "Your training" is already a label in this app: the nav pill on
         * MyTraining, MyTrainingHome and the self-serve page all carry it, and
         * the consent copy links to /my-training as "your training data". A page
         * at /my-history titled the same thing gives two destinations one name,
         * and the one an athlete is told to visit for their sharing settings is
         * not this one.
         *
         * "Lifts" also says what the page holds. It is per-movement — squat,
         * bench, the thing you want to know you are getting stronger at — and it
         * matches the link that opens it ("See how my lifts are going"). */}
        <h1 className="page-title">Your lifts</h1>

        {error && <p className="form-error" role="alert">{error}</p>}

        {/* The empty state is the whole screen, not a note beside a picker with
            nothing in it. Somebody who has logged nothing is not looking at a
            broken page; they are one session away from this working. */}
        {!error && movements.length === 0 && (
          <section className="card history-empty">
            <h2 className="section-title">Nothing logged yet</h2>
            <p className="meta-copy">
              Log a session and it shows up here — every time you train that
              movement again, you can see what you did last time.
            </p>
            <Link className="btn-primary" to="/training">Go to my training</Link>
          </section>
        )}

        {movements.length > 0 && (
          <div className="history-layout">
            <nav className="card history-picker" aria-label="Movements you have trained">
              <p className="kicker">Movements</p>
              <MovementList
                movements={movements}
                selected={selected}
                onSelect={setSelected}
              />
            </nav>

            <section className="card history-detail">
              {history && (
                <>
                  <h2 className="section-title">{history.exerciseName}</h2>
                  {note && <p className="history-note">{note}</p>}
                  <ul className="history-entries">
                    {history.days.map((day, index) => (
                      <HistoryDay
                        key={day.date}
                        day={day}
                        unit={unit}
                        trend={dayTrend(history.days, index)}
                      />
                    ))}
                  </ul>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </BaseLayout>
  );
};

export default TrainingHistoryPage;
export { MovementList, HistoryDay, topSet };
